<template>
  <Popup
    class="filter-popup"
    width="semi-full"
    position="fixed"
    transition="slide-left"
    :show="show"
    @update:show="$emit('update:show', $event)"
  >
    <h2 class="filter-popup__header">
      <div class="icon-wrapper" @click="$emit('update:show', false)">
        <ArrowLeft />
      </div>

      <span>Filter</span>
    </h2>
    <div class="filter-popup__body">
      <template v-if="isSibSearch">
        <NewFilterSectionComponent
          v-for="(filterSection, i) in filteredFilterSections"
          :key="i"
          :toggleFilter="toggleFilter"
          :selectedFilters="selectedFilters"
          :filteredFilterSection="filterSection"
        />
      </template>
      <template v-else>
        <FilterSectionComponent
          v-for="filterSection of filteredFilterSections"
          :key="filterSection.attribute"
          :filter="filterSection"
          :selected-filter="selectedFilters[filterSection.attribute]"
          @toggle-filter="toggleFilter(filterSection.attribute, $event)"
        >
        </FilterSectionComponent>
      </template>
    </div>
    <div class="filter-popup__footer">
      <sib-button class="button-reset" @click="reset"> Reset </sib-button>
      <sib-button role="primary" @click="applyClick"> Apply </sib-button>
    </div>
  </Popup>
</template>

<script lang="ts" setup>
import ArrowLeft from '@/assets/images/icons/arrow-left.svg'
import FilterSectionComponent from './FilterSection.vue'
import NewFilterSectionComponent from './NewFilterSection.vue'
import { Bucket, FilterSection } from '@/types'
import { onMounted, ref, watch } from 'vue'
import { computed } from '@vue/reactivity'
import { useRoute } from 'vue-router'
import {
  COLLECTION_AFTER_QUERY_STRING,
  COLLECTION_BEFORE_QUERY_STRING,
  PAGE_QUERY_STRING,
  SEARCH_RESTRICT_BY_COLLECTIONS_QUERY_STRING,
  SEARCH_RESTRICT_BY_PRODUCT_TYPE_QUERY_STRING,
  SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX,
  SEARCH_RESTRICT_BY_TAG_QUERY_STRING,
  QUERY_STRING_PRODUCT_IDS,
  QUERY_STRING_PRODUCT_HANDLE,
  EVENT_TRACKING_CLICK_COLLECTION_FILTER,
  MIXPANEL_DESTINATION,
  destinationIntegrations,
  cloneDeep,
  SIB_SEARCH,
  EVENT_REMOVE_SELECTED_FILTER,
  EVENT_CHANGE_SELECTED_FILTER,
  EVENT_REMOVE_ALL_SELECTED_FILTER,
} from '@/utils'
import useUpdateRouter from '@/composables/router'
import Popup from '@/ui-kits/Popup.vue'
import Analytics from '@/services/analytics'
import EventBus from '@/services/eventbus'

interface SelectedFilters {
  [key: string]: string[]
}

const props = withDefaults(
  defineProps<{
    show: boolean
    filterSections: FilterSection[]
    pageType?: string
    attributeBasedVisibility: string
  }>(),
  {
    show: false,
    filterSections: () => [],
    attributeBasedVisibility: '',
  }
)

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'apply-filter', hasFilter: boolean): void
}>()

const route = useRoute()
const selectedFilters = ref<SelectedFilters>({})
const filtersOutAttributeSearchanise = ['price', 'reviews_average_score_titles']
const filtersOutAttributeSibSearch = [
  'price',
  'reviews_average_score_titles',
  'collection_titles',
  'tags',
  'niches',
]
const NON_TOPIC_ATTRIBUTE = 'non_topic'
const PAGE_TYPE_COLLECTION = 'collection'

const { updateQueryString, resetQueryString } = useUpdateRouter()

const isSibSearch = computed(
  () => import.meta.env.VITE_SEARCH_TYPE == SIB_SEARCH
)

const filtersOutAttribute = computed(() =>
  isSibSearch.value
    ? filtersOutAttributeSibSearch
    : filtersOutAttributeSearchanise
)

const lastFilteredFilterSections = ref<any>([])
const productTypesAttribute = 'product_types'
const themesAttribute = 'themes'
const lastPrefixParents = '_parents'

const filteredFilterSections = computed(() => {
  if (isSibSearch.value && props.pageType != PAGE_TYPE_COLLECTION) {
    if (props.filterSections?.length) {
      lastFilteredFilterSections.value =
        getFilteredFilterSections(cloneDeep(props.filterSections)) || []
    }

    const filteredFilterSectionsFinal = props.filterSections?.length
      ? getFilteredFilterSections(cloneDeep(props.filterSections))
      : lastFilteredFilterSections.value || []
    return props.attributeBasedVisibility
      ? filteredFilterSectionsFinal.filter(
          (elm) => elm.attribute == props.attributeBasedVisibility
        )
      : filteredFilterSectionsFinal
  } else {
    return (
      props.filterSections?.filter((elm) =>
        props.attributeBasedVisibility
          ? props.attributeBasedVisibility == elm.attribute
          : !filtersOutAttribute.value.includes(elm.attribute)
      ) || []
    )
  }
})

onMounted(() => {
  let hasFilter = false
  // get the querystring and save to selectedFilters
  for (const key in route.query) {
    if (!key.startsWith(SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX)) continue
    if (Object.prototype.hasOwnProperty.call(route.query, key)) {
      const value = route.query[key]
      const attribute = key.split(SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX).pop()
      if (value && value.length) hasFilter = true
      if (typeof value === 'string') {
        selectedFilters.value[attribute!] = [value]
      } else {
        selectedFilters.value[attribute!] = [...(value as string[])]
      }
    }
  }

  emit('apply-filter', hasFilter)

  // Remove the selected filter that does not have any product
  watch(
    () => filteredFilterSections,
    () => {
      let filteredFilterSectionsClone = []
      // use app search Goldenphoenix
      if (isSibSearch.value && props.pageType != PAGE_TYPE_COLLECTION) {
        filteredFilterSectionsClone = cloneDeep(
          filteredFilterSections.value || []
        )
          .map((item: FilterSection) => {
            if (!item.values?.length) return item
            return item.values.map(
              (value: {
                attribute: string
                buckets: Bucket[]
                title: string
              }) => ({
                attribute: `${item.attribute}_${value.attribute}`,
                buckets: value.buckets,
                title: value.title,
              })
            )
          })
          .flat()
      } else {
        filteredFilterSectionsClone = filteredFilterSections.value
      }

      if (!isSibSearch.value) {
        for (const attribute in selectedFilters.value) {
          const filterValues = selectedFilters.value[attribute]
          const index = filteredFilterSectionsClone.findIndex(
            (elm) => elm.attribute == attribute
          )
          if (index > -1) {
            const filterSection = filteredFilterSectionsClone[index]
            const removedIndices: number[] = []
            selectedFilters.value[attribute].forEach((selectedValue, index) => {
              const bucket = filterSection.buckets.find(
                (element) => element.value === selectedValue
              )
              if (!bucket || bucket.count === 0) removedIndices.push(index)
            })
            // sort the removed indices DESC
            removedIndices.sort((a, b) => b - a)
            // remove each item
            for (let i = 0; i < removedIndices.length; i++) {
              filterValues.splice(removedIndices[i], 1)
            }
          } else if (attribute != NON_TOPIC_ATTRIBUTE) {
            selectedFilters.value[attribute] = []
          }
        }
      }
    },
    { deep: true }
  )

  // remove the selected filter when get event remove from SelectedFilter component
  watch(
    () => selectedFilters.value,
    () => {
      EventBus.trigger(
        EVENT_CHANGE_SELECTED_FILTER,
        cloneDeep(selectedFilters.value)
      )
    },
    { deep: true, immediate: true }
  )
  EventBus.on(EVENT_REMOVE_SELECTED_FILTER, (option) =>
    toggleFilter(option.attribute, option.value)
  )
  EventBus.on(EVENT_REMOVE_ALL_SELECTED_FILTER, () => reset())
})

function toggleFilter(attribute: string, value: string) {
  if (!attribute || !value) return
  if (!selectedFilters.value[attribute]) {
    selectedFilters.value[attribute] = []
  }
  const index = selectedFilters.value[attribute].findIndex(
    (elm) => elm == value
  )
  if (attribute == NON_TOPIC_ATTRIBUTE) {
    // because attribute non topic is true or false, so we need replace it
    selectedFilters.value[attribute] = [value]

    // Temporarily hide this event
    // trackingClickCollectionFilter(attribute, value)
  } else if (index == -1) {
    selectedFilters.value[attribute].push(value)

    // Temporarily hide this event
    // trackingClickCollectionFilter(attribute, value)
  } else {
    selectedFilters.value[attribute].splice(index, 1)
  }
  apply()
}

function reset() {
  selectedFilters.value = {}
  apply()
  resetQueryParams()
}

function resetQueryParams() {
  const querySearch = route.query['query']
  const queryParams = querySearch ? { query: querySearch } : {}
  resetQueryString(queryParams)
}

function apply() {
  const additionQueryParams: Record<
    string,
    string | string[] | undefined | number
  > = {
    [SEARCH_RESTRICT_BY_COLLECTIONS_QUERY_STRING]: undefined,
    [SEARCH_RESTRICT_BY_PRODUCT_TYPE_QUERY_STRING]: undefined,
    [SEARCH_RESTRICT_BY_TAG_QUERY_STRING]: undefined,
    [PAGE_QUERY_STRING]: undefined,
    [COLLECTION_AFTER_QUERY_STRING]: undefined,
    [COLLECTION_BEFORE_QUERY_STRING]: undefined,
    [QUERY_STRING_PRODUCT_IDS]: undefined,
    [QUERY_STRING_PRODUCT_HANDLE]: undefined,
  }

  let hasFilter = false
  for (const attribute in selectedFilters.value) {
    if (
      Object.prototype.hasOwnProperty.call(selectedFilters.value, attribute)
    ) {
      const value = selectedFilters.value[attribute]
      additionQueryParams[
        `${SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX}${attribute}`
      ] = value
      if (value && value.length) {
        hasFilter = true
      }
    }
  }

  emit('apply-filter', hasFilter)
  updateQueryString({
    ...additionQueryParams,
  })
}

function applyClick() {
  apply()
  emit('update:show', false)
}

function trackingClickCollectionFilter(
  filteredSection: string,
  filteredValue: string
) {
  Analytics.track(
    EVENT_TRACKING_CLICK_COLLECTION_FILTER,
    {
      path: route.path,
      section: filteredSection,
      filtered_value: filteredValue,
      collection_type: route.name as string,
    },
    destinationIntegrations([MIXPANEL_DESTINATION])
  )
}

function mergeArrays(arr1 = [] as any[], arr2 = [] as any[]) {
  if (arr1.length === 0) return arr2
  if (arr2.length === 0) return arr1
  let mergedArray = [] as any[]
  arr1.forEach((item1) => {
    if (item1.hasOwnProperty('attribute')) {
      let item2 = arr2.find((item) => item?.attribute === item1?.attribute)
      if (item2) {
        if (item1.values && item2.values) {
          item1.values = mergeArrays(item1.values, item2.values)
        }
        if (item1.buckets && item2.buckets) {
          item1.buckets = mergeArrays(item1.buckets, item2.buckets)
        }
        mergedArray.push(item1)
      } else {
        mergedArray.push(item1)
      }
    } else if (item1.hasOwnProperty('value')) {
      let item2 = arr2.find((item) => item.value === item1.value)
      if (item2) {
        mergedArray.push(item2)
      } else {
        mergedArray.push(item1)
      }
    }
  })
  let remainingItems = arr2.filter(
    (item) =>
      !arr1.find(
        (e) =>
          (item.hasOwnProperty('attribute') &&
            e.attribute === item.attribute) ||
          (item.hasOwnProperty('value') && e.value === item.value)
      )
  )
  mergedArray = mergedArray.concat(remainingItems)

  return mergedArray
}

function getFilteredFilterSections(filterSections: any) {
  //merge respone data from api with last filtered section base on selected filter
  const lastFilterSectionBaseOnFilterSelected =
    getLastFilterSectionBaseOnFilterSelected(
      cloneDeep(selectedFilters.value || {}),
      cloneDeep(lastFilteredFilterSections.value)
    )
  const filterSectionOutAttribute = cloneDeep(
    filterSections?.filter(
      (elm) => !filtersOutAttribute.value.includes(elm.attribute)
    ) || []
  )
  const filteredFilterSections = mergeArrays(
    cloneDeep(lastFilterSectionBaseOnFilterSelected),
    cloneDeep(filterSectionOutAttribute)
  )

  return filteredFilterSections || []
}

function getFilterSectionSelected() {
  const selectedFiltersClone = cloneDeep(selectedFilters.value)

  const filterSectionSelected = [
    { title: 'Product Types', attribute: 'product_types', values: [] },
    { title: 'Occasions', attribute: 'occasions', buckets: [] },
    { title: 'Recipients', attribute: 'recipients', buckets: [] },
    { title: 'Themes', attribute: 'themes', values: [] },
    {
      title: 'Personalization Type',
      attribute: 'personalization_type',
      buckets: [],
    },
  ] as any
  if (Object.keys(selectedFiltersClone)?.length) {
    Object.keys(selectedFiltersClone).forEach((key) => {
      if (key == 'non_topic') return
      if (key.endsWith(lastPrefixParents)) {
        const index = filterSectionSelected.findIndex(
          (item) => item.attribute == key.split(lastPrefixParents)[0]
        )
        if (index == -1) {
          filterSectionSelected[index].values = selectedFiltersClone[key].map(
            (item) => ({
              title: formatTitle(item),
              attribute: item,
              buckets: [],
            })
          )
        }
      } else if (
        (key.startsWith(productTypesAttribute) ||
          key.startsWith(themesAttribute)) &&
        !key.endsWith(lastPrefixParents)
      ) {
        const attributeParent = key.startsWith(productTypesAttribute)
          ? productTypesAttribute
          : themesAttribute
        const index = filterSectionSelected.findIndex(
          (item) => item.attribute == attributeParent
        )
        filterSectionSelected[index].values.push({
          attribute: key.split(`${attributeParent}_`)[1],
          title: formatTitle(key.split(`${attributeParent}_`)[1]),
          buckets: selectedFiltersClone[key].map((item) => ({
            value: item,
            count: 0,
          })),
        })
      } else if (
        !key.startsWith(productTypesAttribute) &&
        !key.startsWith(themesAttribute)
      ) {
        const index = filterSectionSelected.findIndex(
          (item) => item.attribute == key
        )
        filterSectionSelected[index].buckets = selectedFiltersClone[key].map(
          (item) => ({
            value: item,
            count: 0,
          })
        )
      }
    })
  }
  return filterSectionSelected
}

function getLastFilterSectionBaseOnFilterSelected(object: any, array: any[]) {
  let lastFilterSection = [] as any[]
  Object.keys(object).forEach((key) => {
    if (
      !key.startsWith(productTypesAttribute) &&
      !key.startsWith(themesAttribute) &&
      object[key].length
    ) {
      const filterObject = array.find((item) => item.attribute == key)
      if (filterObject) lastFilterSection.push(filterObject)
    } else if (key.includes('product_types') && object[key].length) {
      const indexOfArray = array.findIndex(
        (item) => item.attribute == productTypesAttribute
      )
      lastFilterSection.push(array[indexOfArray])
    } else if (key.includes('themes') && object[key].length) {
      const indexOfArray = array.findIndex(
        (item) => item.attribute == themesAttribute
      )
      lastFilterSection.push(array[indexOfArray])
    }
  })

  const filterSectionSelected = getFilterSectionSelected()
  const lastFilterSectionBaseOnFilterSelected = mergeArrays(
    cloneDeep(filterSectionSelected),
    cloneDeep(lastFilterSection)
  )

  return resetCountValue(lastFilterSectionBaseOnFilterSelected)
}

function resetCountValue(array: any[]) {
  let newArray = [...array]
  newArray.forEach((item) => {
    if (item.hasOwnProperty('values')) {
      item.values.forEach((value) => {
        if (value.hasOwnProperty('buckets')) {
          value.buckets.forEach((bucket) => {
            if (bucket.hasOwnProperty('count')) {
              bucket.count = 0
            }
          })
        }
      })
    } else if (item.hasOwnProperty('buckets')) {
      item.buckets.forEach((bucket) => {
        if (bucket.hasOwnProperty('count')) {
          bucket.count = 0
        }
      })
    }
  })

  return newArray
}

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

function formatTitle(title: string) {
  return capitalizeFirstLetter(title.replaceAll('-', ' '))
}
</script>

<style lang="scss">
.filter-popup {
  --filer-popup-header-height: 3.5rem;
  --filer-popup-footer-height: 4rem;

  .popup__content {
    overflow: auto;
    padding: 0 1em;
    max-width: 80%;
  }

  &__header {
    font-size: 1.2em;
    display: flex;
    align-items: center;
    border-bottom: solid 1px var(--border-color);
    margin: 0;
    position: sticky;
    top: 0;
    left: 0;
    width: 100%;
    height: var(--filer-popup-header-height);
    background-color: #fff;
    z-index: 1;

    span {
      width: 100%;
      text-align: center;
    }

    svg {
      width: 1em;
      height: 1em;
    }
  }

  &__body {
    padding: 0.5em 0 5em;
    min-height: 80%;
  }

  &__footer {
    display: flex;
    gap: 1em;
    position: sticky;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 0.75em 0;
    border-top: solid 1px var(--border-color);
    background-color: #fff;

    .button-reset {
      width: auto;
    }
  }
}
</style>
